import React, { useState } from 'react';
import { actions, overlays, inputs } from '@ecosystem/newron-design-system';
import { MAX_GROUP_NAME_LENGTH } from './constants';
import { checkForHTMLTags, sanitizeInput } from '../../common/util';

const { Button } = actions;
const { Modal } = overlays;
const { TextInput } = inputs;

export enum ActionType {
  Delete = 'delete',
  Rename = 'rename',
}

interface UpdateProductGroupModalProps {
  type: ActionType;
  group: string;
  allGroups: string[];
  isOpen: boolean;
  onClose: (e) => void;
  onAction: (groupName: string) => void;
}

export const UpdateProductGroupModal = ({
  group,
  isOpen,
  onClose,
  onAction,
  type,
  allGroups,
}: UpdateProductGroupModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newGroupName, setNewGroupName] = useState<string>(group);
  const [isGroupNameError, setGroupNameError] = useState('');

  const submit = async e => {
    e.stopPropagation();
    setIsSubmitting(true);
    const groupName = type === ActionType.Delete ? group : newGroupName;
    onAction(groupName);
    setIsSubmitting(false);
  };

  const deleteConfirmationLabel = 'Yes, delete';
  const header = type === ActionType.Delete ? 'Delete group?' : 'Rename group';
  const confirmButtonText = type === ActionType.Delete ? deleteConfirmationLabel : 'Apply';
  const isDanger = confirmButtonText === deleteConfirmationLabel;
  const cancelButtonText = type === ActionType.Delete ? "Don't delete" : 'Cancel';

  const groupNameExists = allGroups?.some(name => name === newGroupName);
  const isActionRename = type === ActionType.Rename;

  const renameDisableButton = newGroupName === group || newGroupName === '' || groupNameExists;

  const isConfirmButtonDisabled = isSubmitting || (isActionRename && renameDisableButton) || !!isGroupNameError;

  const handleClose = e => {
    setNewGroupName(group);
    onClose(e);
  };

  const handleInputChange = (input: string): void => {
    const sanitizedInput = sanitizeInput(input);
    const error = checkForHTMLTags(sanitizedInput);
    if (error) {
      setGroupNameError(error);
    } else {
      setGroupNameError('');
    }
    setNewGroupName(sanitizedInput);
  };

  return (
    <Modal isOpen={isOpen}>
      <Modal.Header>
        <h2>{header}</h2>
      </Modal.Header>
      <Modal.Body>
        {type === 'rename' ? (
          <TextInput
            name="rename-group"
            label="New name"
            value={newGroupName}
            placeholder=""
            onChange={handleInputChange}
            maxLength={MAX_GROUP_NAME_LENGTH}
            errorMessage={groupNameExists ? 'Group already exists, try another name' : isGroupNameError}
          />
        ) : (
          <>
            <p className="font-bold text-md">"{group}" will be deleted immediately</p>
            <p className="text-md">You can't undo this action</p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer align="right">
        <div className="flex gap-4">
          <div onClick={e => handleClose(e)} aria-hidden="true">
            <Button mode="secondary" onClick={() => {}}>
              {cancelButtonText}
            </Button>
          </div>
          <div onClick={e => submit(e)} aria-hidden="true">
            <Button isDanger={isDanger} disabled={isConfirmButtonDisabled} onClick={() => {}}>
              {confirmButtonText}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
